import { ReactNode } from 'react'

export type StatusType = 'success' | 'warning' | 'error' | 'info' | 'default' | 'inactive'

export interface StatusIndicatorProps {
  /**
   * The status type which determines the color
   */
  status: StatusType

  /**
   * Label text to display
   */
  label?: string

  /**
   * Size of the indicator dot
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'

  /**
   * Whether to show the dot indicator
   * @default true
   */
  showDot?: boolean

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Children to render (alternative to label)
   */
  children?: ReactNode
}

/**
 * Color mapping for status types
 */
const STATUS_COLORS: Record<StatusType, { dot: string; text: string }> = {
  success: { dot: 'bg-green-500', text: 'text-green-700' },
  warning: { dot: 'bg-yellow-500', text: 'text-yellow-700' },
  error: { dot: 'bg-red-500', text: 'text-red-700' },
  info: { dot: 'bg-blue-500', text: 'text-blue-700' },
  default: { dot: 'bg-gray-500', text: 'text-gray-700' },
  inactive: { dot: 'bg-gray-400', text: 'text-gray-500' },
}

/**
 * Size classes for the dot
 */
const SIZE_CLASSES: Record<NonNullable<StatusIndicatorProps['size']>, string> = {
  small: 'w-1.5 h-1.5',
  medium: 'w-2 h-2',
  large: 'w-2.5 h-2.5',
}

/**
 * StatusIndicator Component
 *
 * Displays a status indicator with a colored dot and optional label.
 *
 * @example
 * // Basic usage
 * <StatusIndicator status="success" label="Active" />
 *
 * @example
 * // Without label
 * <StatusIndicator status="error" />
 *
 * @example
 * // Different sizes
 * <StatusIndicator status="warning" label="Pending" size="large" />
 *
 * @example
 * // With children instead of label
 * <StatusIndicator status="info">
 *   <span className="font-medium">Processing</span>
 * </StatusIndicator>
 */
export function StatusIndicator({
  status,
  label,
  size = 'medium',
  showDot = true,
  className = '',
  children,
}: StatusIndicatorProps) {
  const colors = STATUS_COLORS[status]
  const sizeClass = SIZE_CLASSES[size]

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {showDot && (
        <span className={`${sizeClass} rounded-full ${colors.dot}`} aria-hidden="true" />
      )}
      {(label || children) && (
        <span className={`capitalize ${colors.text}`}>
          {children || label}
        </span>
      )}
    </div>
  )
}

export interface StatusDotProps {
  /**
   * The status type which determines the color
   */
  status: StatusType

  /**
   * Size of the dot
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'

  /**
   * Additional CSS classes
   */
  className?: string

  /**
   * Accessible label for screen readers
   */
  ariaLabel?: string
}

/**
 * StatusDot Component
 *
 * A standalone status dot without label.
 *
 * @example
 * <StatusDot status="success" />
 *
 * @example
 * <StatusDot status="error" size="large" ariaLabel="Error status" />
 */
export function StatusDot({
  status,
  size = 'medium',
  className = '',
  ariaLabel,
}: StatusDotProps) {
  const colors = STATUS_COLORS[status]
  const sizeClass = SIZE_CLASSES[size]

  return (
    <span
      className={`${sizeClass} rounded-full ${colors.dot} ${className}`}
      role="status"
      aria-label={ariaLabel || status}
    />
  )
}

/**
 * Helper function to convert common status strings to StatusType
 */
export function getStatusType(status: string): StatusType {
  const statusMap: Record<string, StatusType> = {
    active: 'success',
    enabled: 'success',
    online: 'success',
    completed: 'success',
    approved: 'success',
    live: 'success',

    inactive: 'inactive',
    disabled: 'inactive',
    offline: 'inactive',
    draft: 'inactive',

    pending: 'warning',
    processing: 'warning',
    waiting: 'warning',

    error: 'error',
    failed: 'error',
    rejected: 'error',
    cancelled: 'error',
    archived: 'error',
    purged: 'error',

    info: 'info',
    new: 'info',
  }

  return statusMap[status.toLowerCase()] || 'default'
}

// Export color mapping for external use
export { STATUS_COLORS }
